import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { format, parseISO } from 'date-fns';
import ReportLayout, { ReportTable, usePagination } from './ReportLayout';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import clsx from 'clsx';

const fmtDate = v => { try { return v ? format(parseISO(v), 'd MMM yyyy') : '–'; } catch { return v || '–'; } };
const fmtCurrency = v => v ? `$${parseFloat(v).toFixed(2)}` : '$0.00';

const COLS = [
  { key: 'full_name',          label: 'Client Name' },
  { key: 'dob',               label: 'DOB',          render: fmtDate },
  { key: 'gender',            label: 'Gender',        render: v => v || '–' },
  { key: 'ndis_number',       label: 'NDIS Number',   render: v => v || '–' },
  { key: 'client_type',       label: 'Client Type',   render: v => v ? v.replace(/_/g, ' ') : '–' },
  { key: 'address',           label: 'Address',       render: v => v || '–' },
  { key: 'suburb',            label: 'Suburb',        render: v => v || '–' },
  { key: 'state',             label: 'State',         render: v => v || '–' },
  { key: 'plan_period_start', label: 'Plan Start',    render: fmtDate },
  { key: 'plan_period_end',   label: 'Plan End',      render: fmtDate },
  { key: 'primary_disability',label: 'Primary Disability', render: v => v || '–' },
  { key: 'total_budget',      label: 'Total Budget ($)',  render: fmtCurrency },
  { key: 'total_spent',       label: 'Total Spent ($)',   render: fmtCurrency },
  {
    key: '_remaining',
    label: 'Remaining ($)',
    render: (v, row) => {
      const rem = parseFloat(row.total_budget || 0) - parseFloat(row.total_spent || 0);
      return <span className={rem < 0 ? 'text-red-600 font-bold' : 'text-green-600'}>${rem.toFixed(2)}</span>;
    },
  },
  { key: 'doc_count',          label: 'Docs Count',    render: v => v || 0 },
  { key: 'goal_count',         label: 'Goals Count',   render: v => v || 0 },
  { key: 'care_plan_status',  label: 'Care Plan Status', render: v => v || '–' },
];

export default function ClientProfileReport() {
  const [data, setData]               = useState([]);
  const [loading, setLoading]         = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [clients, setClients]         = useState([]);
  const [clientId, setClientId]       = useState('');
  const [pendingClientId, setPending] = useState('');

  const pagination = usePagination(data);

  const load = useCallback((cid) => {
    setLoading(true);
    const p = new URLSearchParams();
    if (cid) p.set('client_id', cid);
    api.get(`/reports/client-profile${p.toString() ? `?${p}` : ''}`)
      .then(r => { setData(r.data || []); pagination.reset(); })
      .catch(() => toast.error('Failed to load'))
      .finally(() => setLoading(false));
  }, []);

  // Load client list for dropdown
  useEffect(() => {
    api.get('/clients')
      .then(r => setClients(r.data || []))
      .catch(() => {});
  }, []);

  useEffect(() => { load(''); }, [load]);

  const handleApply = () => {
    setClientId(pendingClientId);
    load(pendingClientId);
  };

  const summary = useMemo(() => ({
    count:       data.length,
    totalBudget: data.reduce((s, r) => s + parseFloat(r.total_budget || 0), 0),
    totalSpent:  data.reduce((s, r) => s + parseFloat(r.total_spent  || 0), 0),
  }), [data]);

  const selectedClient = clients.find(c => String(c.id) === String(clientId));

  const chips = [
    ...(clientId && selectedClient ? [{ label: 'Client', value: selectedClient.first_name + ' ' + selectedClient.last_name, onRemove: () => { setClientId(''); setPending(''); load(''); } }] : []),
  ];

  return (
    <ReportLayout
      title="Client Profile Report"
      data={data}
      filename="client_profile_report"
      columns={COLS}
      activeChips={chips}
      showFiltersPanel={showFilters}
      onToggleFilters={() => setShowFilters(v => !v)}
      filters={
        <div className="flex flex-wrap gap-4">
          <div>
            <label className="block text-xs font-semibold text-slate-500 mb-1">Client</label>
            <select
              value={pendingClientId}
              onChange={e => setPending(e.target.value)}
              className="input-field !w-auto text-sm"
            >
              <option value="">All Clients</option>
              {clients.map(c => (
                <option key={c.id} value={c.id}>
                  {c.first_name} {c.last_name}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-end">
            <button onClick={handleApply} className="btn-primary text-sm px-4 py-2">Apply</button>
          </div>
        </div>
      }
    >
      <div className="grid grid-cols-3 gap-4 mb-4">
        {[
          { label: 'Total Clients',  value: summary.count,                                          color: 'text-slate-900' },
          { label: 'Total Budget',   value: `$${summary.totalBudget.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, color: 'text-blue-600' },
          { label: 'Total Spent',    value: `$${summary.totalSpent.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,  color: 'text-amber-600' },
        ].map(card => (
          <div key={card.label} className="bg-white border border-slate-200 rounded-xl p-4">
            <div className="text-xs text-slate-500 mb-1">{card.label}</div>
            <div className={clsx('text-2xl font-bold', card.color)}>{card.value}</div>
          </div>
        ))}
      </div>
      <ReportTable
        columns={COLS}
        data={data}
        loading={loading}
        pagination={pagination}
        emptyMsg="No client profiles found."
      />
    </ReportLayout>
  );
}
